import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { isGovernmentCompany } from "@features/licensing/schema/licenseRequestValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildFormDraftFromBaseline,
  clearFormDraft,
  mergeFormDraft,
  readFormDraft,
  writeFormDraft,
} from "@hooks/formDraftPersistence";
import { useCompanyTypes } from "@hooks/useCompanyTypes";
import {
  VENDOR_LICENSE_REQUEST_FIELDS as F,
  tradeLicenseIssuingAuthorities,
} from "@pages/vendor-license-request/constants";
import { useExternalDocumentUpload } from "@pages/vendor-license-request/hooks/useExternalDocumentUpload";
import { useSyncCompanyType } from "@pages/vendor-license-request/hooks/useSyncCompanyType";
import {
  getGovernmentVendorLicenseRequestFormSchema,
  getVendorLicenseRequestFormSchema,
  mapVendorLicenseRequestToPayload,
  vendorLicenseRequestDefaultValues,
  type VendorLicenseRequestFormData,
} from "@pages/vendor-license-request/schema/vendorLicenseRequestFormSchema";
import {
  getCompanyTypeOptions,
  mapVendorLicenseToRenewalForm,
} from "@pages/vendor-license-request/utils";
import VendorLicenseRequestFormContent from "@pages/vendor-license-request/VendorLicenseRequestFormContent";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getVendorLicenseById } from "@services/licensingApi";
import { SasTokenPrefix } from "@services/user/types";
import { useQuery } from "@tanstack/react-query";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateVendorLicenseRenewalRequest } from "./useCreateVendorLicenseRenewalRequest";

const TEST_ID_BASE = "vendor-license-renewal";
const LANG_BASE = "licensing";
const DRAFT_KEY_PREFIX = "vendor-license-renewal-draft";
const getUploadStoreKey = (field: string) => `vendor-license-renewal:${field}`;
const DRAFT_IGNORED_FIELDS: string[] = [
  F.TRADE_LICENSE_DOCUMENT_URL,
  F.COMPANY_INCORPORATION_DOCUMENT_URL,
];
const DRAFT_PERSISTED_FIELDS = (Object.values(F) as string[]).filter(
  (field) => !DRAFT_IGNORED_FIELDS.includes(field),
);

const VendorLicenseRenewalPage = () => {
  const { renewalId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const { notify } = useNotification();
  const user = useAuthStore((state) => state.user ?? null);
  const userCompanyId = user?.companyId ?? null;
  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);
  const draftKey = `${DRAFT_KEY_PREFIX}-${renewalId ?? "unknown"}`;
  const createRenewalMutation = useCreateVendorLicenseRenewalRequest(renewalId ?? "");
  const {
    data: companyTypes = [],
    isLoading: isCompanyTypesLoading,
    isError: isCompanyTypesError,
    error: companyTypesError,
  } = useCompanyTypes();
  const {
    data: license,
    isLoading: isLicenseLoading,
    isError: isLicenseError,
    error: licenseError,
  } = useQuery({
    queryKey: ["vendorLicense", "renewal", renewalId],
    queryFn: () => getVendorLicenseById(renewalId as string),
    enabled: Boolean(renewalId),
  });
  const baseValues = useMemo(
    () =>
      license
        ? {
            ...mapVendorLicenseToRenewalForm(license, user),
            tradeLicenseDocumentUrl: [],
          }
        : vendorLicenseRequestDefaultValues,
    [license, user],
  );
  const licenseCompanyId = license?.taskDecision?.companyId ?? license?.companyId;
  const canAccessRenewal = Boolean(
    userCompanyId && licenseCompanyId && userCompanyId === licenseCompanyId,
  );
  const draftValues = useMemo(
    () => readFormDraft<VendorLicenseRequestFormData>(draftKey),
    [draftKey],
  );
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const suspendDraftPersistenceRef = useRef(false);
  const hasInitialDraft = !!draftValues && Object.keys(draftValues).length > 0;

  const schema = useMemo(
    () => getVendorLicenseRequestFormSchema(t, companyTypes),
    [t, companyTypes],
  );
  const governmentSchema = useMemo(
    () => getGovernmentVendorLicenseRequestFormSchema(t, companyTypes),
    [t, companyTypes],
  );
  const form = useForm<VendorLicenseRequestFormData>({
    resolver: (values, context, options) =>
      zodResolver(
        isGovernmentCompany(companyTypes, values.companyTypes) ? governmentSchema : schema,
      )(values, context, { ...options }),
    defaultValues: vendorLicenseRequestDefaultValues,
    mode: "onBlur",
  });
  const { watch } = form;
  const { isVendorDisabled } = useSyncCompanyType(
    companyTypes,
    form,
    isCompanyTypesLoading,
    isCompanyTypesError,
  );
  const companyTypeOptions = getCompanyTypeOptions(companyTypes, isVendorDisabled);

  const tradeLicenseIssuingAuthorityOptions = useMemo(
    () =>
      tradeLicenseIssuingAuthorities.map((authority) => ({
        value: authority.value,
        label: authority.label,
      })),
    [],
  );
  const fileUploadProfiles = useFileUploadProfiles();
  const documentConditions = useMemo(
    () => ({ ...fileUploadProfiles.certificates, hideErrors: false }),
    [fileUploadProfiles.certificates],
  );
  const supportedDocumentExtensions = useMemo(
    () => documentConditions.format.map((extension) => `.${extension.toLowerCase()}`),
    [documentConditions.format],
  );
  const uploadDescription = useMemo(
    () =>
      t("upload.additional-documents-upload", {
        extensions: supportedDocumentExtensions.join(", "),
        size: documentConditions.maxSizeLabel,
      }),
    [documentConditions.maxSizeLabel, supportedDocumentExtensions, t],
  );
  const tradeLicenseUpload = useExternalDocumentUpload(
    F.TRADE_LICENSE_DOCUMENT_URL,
    SasTokenPrefix.LICENSE_SERVICE,
    form,
    "TRADE_LICENSE",
    "VendorLicenseRenewal",
    getUploadStoreKey(F.TRADE_LICENSE_DOCUMENT_URL),
  );
  const companyIncorpUpload = useExternalDocumentUpload(
    F.COMPANY_INCORPORATION_DOCUMENT_URL,
    SasTokenPrefix.LICENSE_SERVICE,
    form,
    "COMPANY_INCORPORATION",
    "VendorLicenseRenewal",
    getUploadStoreKey(F.COMPANY_INCORPORATION_DOCUMENT_URL),
  );
  const tradeLicenseUploadEntriesRef = useRef(tradeLicenseUpload.uploadEntries);
  const companyIncorpUploadEntriesRef = useRef(companyIncorpUpload.uploadEntries);
  tradeLicenseUploadEntriesRef.current = tradeLicenseUpload.uploadEntries;
  companyIncorpUploadEntriesRef.current = companyIncorpUpload.uploadEntries;

  useEffect(() => {
    if (!license) return;
    const mergedValues = mergeFormDraft(baseValues, draftValues);
    const tradeLicenseEntries = (mergedValues.tradeLicenseDocumentUrl ?? []) as UploadEntry[];
    const companyIncorpEntries = (mergedValues.companyIncorporationDocumentUrl ??
      []) as UploadEntry[];

    form.reset(mergedValues);

    if (!tradeLicenseUploadEntriesRef.current.length && tradeLicenseEntries.length) {
      tradeLicenseUpload.setUploadEntries(tradeLicenseEntries);
    }
    if (!companyIncorpUploadEntriesRef.current.length && companyIncorpEntries.length) {
      companyIncorpUpload.setUploadEntries(companyIncorpEntries);
    }
  }, [
    baseValues,
    companyIncorpUpload.setUploadEntries,
    draftValues,
    form,
    license,
    tradeLicenseUpload.setUploadEntries,
  ]);

  useEffect(() => {
    if (!renewalId || isLicenseLoading || !license) return;
    if (canAccessRenewal) return;

    navigate(
      `/${paths.licenses.index}/${paths.licenses.licenseDetailsById.replace(":id", renewalId)}`,
      { replace: true },
    );
  }, [canAccessRenewal, isLicenseLoading, license, navigate, renewalId]);

  useEffect(() => {
    const subscription = watch((values) => {
      if (suspendDraftPersistenceRef.current) return;

      const draft = buildFormDraftFromBaseline(
        values,
        baseValues,
        DRAFT_PERSISTED_FIELDS,
        DRAFT_IGNORED_FIELDS,
      );

      if (Object.keys(draft).length > 0) {
        writeFormDraft(draftKey, draft);
        return;
      }

      if (hasInitialDraft) {
        writeFormDraft(draftKey, draftValues!);
        return;
      }

      clearFormDraft(draftKey);
    });

    return () => subscription.unsubscribe();
  }, [baseValues, draftKey, draftValues, hasInitialDraft, watch]);

  useEffect(() => {
    if (
      isCompanyTypesLoading ||
      isCompanyTypesError ||
      companyTypesError ||
      isLicenseLoading ||
      isLicenseError ||
      licenseError ||
      !license ||
      hasTrackedInitialLoad.current
    ) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("MarHub Access Renewal");
    trackScreenLoad("Licenses", "MarHub Access Renewal", startedAt.current);
  }, [
    companyTypesError,
    isCompanyTypesError,
    isCompanyTypesLoading,
    isLicenseError,
    isLicenseLoading,
    license,
    licenseError,
  ]);

  if (renewalId && license && !canAccessRenewal) {
    return null;
  }

  const onSubmit = async (values: VendorLicenseRequestFormData) => {
    const payload = mapVendorLicenseRequestToPayload(values, companyTypes);

    createRenewalMutation.mutate(payload, {
      onSuccess: () => {
        suspendDraftPersistenceRef.current = true;
        clearFormDraft(draftKey);
        tradeLicenseUpload.handleRemove();
        companyIncorpUpload.handleRemove();
        form.reset(baseValues);
      },
    });
  };

  const isUploading = tradeLicenseUpload.isPending || companyIncorpUpload.isPending;

  const handleCancel = () => {
    if (isUploading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    suspendDraftPersistenceRef.current = true;
    clearFormDraft(draftKey);
    tradeLicenseUpload.handleRemove();
    companyIncorpUpload.handleRemove();
    form.reset(baseValues);
    navigate(
      `/${paths.licenses.index}/${paths.licenses.licenseDetailsById.replace(":id", renewalId ?? "")}`,
    );
  };

  const cancelDialogProps = {
    testId: `${TEST_ID_BASE}-cancel-dialog`,
    title: t(`${LANG_BASE}.cancel-dialog-title`),
    cancelButtonText: t("common.no"),
    confirmButtonText: t("common.yes"),
    handleCloseDialog: () => setIsCancelDialogOpen(false),
    confirmAction: () => {
      setIsCancelDialogOpen(false);
      handleCancel();
    },
    elementContent: (
      <div className="text-left">
        <p>{t(`${LANG_BASE}.cancel-dialog-description`)}</p>
      </div>
    ),
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (isUploading) {
      event.preventDefault();
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    void form.handleSubmit(onSubmit)(event);
  };

  return (
    <VendorLicenseRequestFormContent
      isTradeLicenseRequired={!isGovernmentCompany(companyTypes, watch(F.COMPANY_TYPES))}
      dir={dir}
      form={form}
      testIdBase={TEST_ID_BASE}
      texts={{
        title: t(`${LANG_BASE}.vendor-license-renewal-title`),
        requiredInformation: t(`${LANG_BASE}.vendor-license-request-required-information`),
        attachmentsIntro: t(`${LANG_BASE}.vendor-license-request-attachments-intro`),
        tradeLicenseDocTitle: t(`${LANG_BASE}.vendor-license-request-trade-license-doc-title`),
        companyIncorporationDocTitle: t(
          `${LANG_BASE}.vendor-license-request-company-incorporation-doc-title`,
        ),
        companyName: t(`${LANG_BASE}.vendor-license-request-company-name`),
        companyNameArabic: t(`${LANG_BASE}.vendor-license-request-company-name-arabic`),
        companyType: t(`${LANG_BASE}.vendor-license-request-company-type`),
        natureOfBusiness: t(`${LANG_BASE}.vendor-license-request-nature-of-business`),
        companyEmail: t(`${LANG_BASE}.vendor-license-request-company-email`),
        companyAddress: t(`${LANG_BASE}.vendor-license-request-company-address`),
        applicantName: t(`${LANG_BASE}.vendor-license-request-name-of-applicant`),
        applicantPosition: t(`${LANG_BASE}.vendor-license-request-applicant-position`),
        applicantEmail: t(`${LANG_BASE}.vendor-license-request-email-address`),
        telephoneNumber: t(`${LANG_BASE}.vendor-license-request-telephone-no`),
        mobileNumber: t(`${LANG_BASE}.vendor-license-request-mobile-number`),
        faxNumber: t(`${LANG_BASE}.vendor-license-request-fax-number`),
        tradingLicenseNumber: t(`${LANG_BASE}.vendor-license-request-trade-license-number`),
        tradingLicenseValidityDate: t(
          `${LANG_BASE}.vendor-license-request-trade-license-validity-date`,
        ),
        tradeLicenseIssuingAuthority: t(
          `${LANG_BASE}.vendor-license-request-trade-license-issuing-authority`,
        ),
        numberOfEmployees: t(`${LANG_BASE}.vendor-license-request-no-of-employees`),
        numberOfVehicles: t(`${LANG_BASE}.vendor-license-request-no-of-vehicles`),
        purposeOfRequest: t(`${LANG_BASE}.vendor-license-request-purpose-of-request`),
        cancel: t("common.cancel"),
        submit: t(`${LANG_BASE}.vendor-license-request-submit`),
        translate: t("common.translate"),
      }}
      companyTypeOptions={companyTypeOptions}
      tradeLicenseIssuingAuthorityOptions={tradeLicenseIssuingAuthorityOptions}
      supportedDocumentExtensions={supportedDocumentExtensions}
      documentConditions={documentConditions}
      uploadDescription={uploadDescription}
      tradeLicenseUpload={tradeLicenseUpload}
      companyIncorpUpload={companyIncorpUpload}
      submitDisabled={createRenewalMutation.isPending || form.formState.isSubmitting}
      onCancelClick={() => setIsCancelDialogOpen(true)}
      onFormSubmit={handleFormSubmit}
      cancelDialogOpen={isCancelDialogOpen}
      cancelDialogProps={cancelDialogProps}
      readOnlyApplicantEmail={true}
      disableNativeNumberPattern={true}
    />
  );
};

export default VendorLicenseRenewalPage;
